import React, { useState, useEffect } from 'react';
import { Share, ChevronDown, HelpCircle } from 'lucide-react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2
  }).format(amount);
};

const formatPercentage = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value / 100);
};

// Option pricing calculation using Black-Scholes Model
const calculateOptionPrice = (params) => {
  const {
    spotPrice,
    strikePrice,
    interestRate,
    volatility,
    timeToExpiry,
    optionType,
    optionStyle
  } = params;

  // Convert percentages to decimals
  const r = interestRate / 100;
  const sigma = volatility / 100;
  const T = timeToExpiry / 365; // Convert days to years

  // Black-Scholes formula
  const d1 = (Math.log(spotPrice / strikePrice) + (r + Math.pow(sigma, 2) / 2) * T) / (sigma * Math.sqrt(T));
  const d2 = d1 - sigma * Math.sqrt(T);

  // Standard normal cumulative distribution function
  const cdf = (x) => {
    const a1 = 0.254829592;
    const a2 = -0.284496736;
    const a3 = 1.421413741;
    const a4 = -1.453152027;
    const a5 = 1.061405429;
    const p = 0.3275911;
    
    const sign = x < 0 ? -1 : 1;
    x = Math.abs(x) / Math.sqrt(2.0);
    
    const t = 1.0 / (1.0 + p * x);
    const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
    
    return 0.5 * (1.0 + sign * y);
  };

  let optionPrice = 0;
  let delta = 0, gamma = 0, theta = 0, vega = 0, rho = 0;

  if (optionType === 'call') {
    optionPrice = spotPrice * cdf(d1) - strikePrice * Math.exp(-r * T) * cdf(d2);
    
    // Greeks for call option
    delta = cdf(d1);
    gamma = Math.exp(-Math.pow(d1, 2) / 2) / (spotPrice * sigma * Math.sqrt(T) * Math.sqrt(2 * Math.PI));
    theta = -(spotPrice * sigma * Math.exp(-Math.pow(d1, 2) / 2)) / (2 * Math.sqrt(T) * Math.sqrt(2 * Math.PI)) 
      - r * strikePrice * Math.exp(-r * T) * cdf(d2);
    theta = theta / 365; // Convert to daily theta
    vega = spotPrice * Math.sqrt(T) * Math.exp(-Math.pow(d1, 2) / 2) / (100 * Math.sqrt(2 * Math.PI));
    rho = strikePrice * T * Math.exp(-r * T) * cdf(d2) / 100;
  } else {
    optionPrice = strikePrice * Math.exp(-r * T) * cdf(-d2) - spotPrice * cdf(-d1);
    
    // Greeks for put option
    delta = cdf(d1) - 1;
    gamma = Math.exp(-Math.pow(d1, 2) / 2) / (spotPrice * sigma * Math.sqrt(T) * Math.sqrt(2 * Math.PI));
    theta = -(spotPrice * sigma * Math.exp(-Math.pow(d1, 2) / 2)) / (2 * Math.sqrt(T) * Math.sqrt(2 * Math.PI)) 
      + r * strikePrice * Math.exp(-r * T) * cdf(-d2);
    theta = theta / 365; // Convert to daily theta
    vega = spotPrice * Math.sqrt(T) * Math.exp(-Math.pow(d1, 2) / 2) / (100 * Math.sqrt(2 * Math.PI));
    rho = -strikePrice * T * Math.exp(-r * T) * cdf(-d2) / 100;
  }

  // For European options, we're done
  if (optionStyle === 'european') {
    return {
      optionPrice,
      intrinsicValue: optionType === 'call' 
        ? Math.max(0, spotPrice - strikePrice)
        : Math.max(0, strikePrice - spotPrice),
      timeValue: optionPrice - (optionType === 'call' 
        ? Math.max(0, spotPrice - strikePrice)
        : Math.max(0, strikePrice - spotPrice)),
      delta,
      gamma,
      theta,
      vega,
      rho,
      breakEvenPrice: optionType === 'call' 
        ? strikePrice + optionPrice
        : strikePrice - optionPrice
    };
  } else {
    // For American options (simplified approximation)
    // American options can be exercised early, so intrinsic value matters
    const intrinsicValue = optionType === 'call' 
      ? Math.max(0, spotPrice - strikePrice)
      : Math.max(0, strikePrice - spotPrice);
    
    // American option price is approximately the maximum of European price and intrinsic value
    const americanOptionPrice = Math.max(optionPrice, intrinsicValue);
    
    return {
      optionPrice: americanOptionPrice,
      intrinsicValue,
      timeValue: americanOptionPrice - intrinsicValue,
      delta,
      gamma,
      theta,
      vega,
      rho,
      breakEvenPrice: optionType === 'call' 
        ? strikePrice + americanOptionPrice
        : strikePrice - americanOptionPrice
    };
  }
};

const OptionValueCalculator = () => {
  // State for basic inputs
  const [spotPrice, setSpotPrice] = useState(18000);
  const [strikePrice, setStrikePrice] = useState(18500);
  const [timeToExpiry, setTimeToExpiry] = useState(30);
  const [optionType, setOptionType] = useState('call');
  
  // State for volatility and other parameters
  const [volatility, setVolatility] = useState(22);
  const [interestRate, setInterestRate] = useState(6.5);
  const [lotSize, setLotSize] = useState(50);
  
  // State for advanced settings
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [optionStyle, setOptionStyle] = useState('european');
  const [dividendYield, setDividendYield] = useState(0);
  
  // State for share toast
  const [showShareToast, setShowShareToast] = useState(false);
  
  // State for tooltips
  const [showToolTip, setShowToolTip] = useState('');
  
  // State for calculation results
  const [result, setResult] = useState({
    optionPrice: 0,
    intrinsicValue: 0,
    timeValue: 0,
    delta: 0,
    gamma: 0,
    theta: 0,
    vega: 0,
    rho: 0,
    breakEvenPrice: 0
  });

  // Recalculate whenever inputs change
  useEffect(() => {
    const params = {
      spotPrice,
      strikePrice,
      interestRate,
      volatility,
      timeToExpiry,
      optionType,
      optionStyle,
      dividendYield
    };
    
    setResult(calculateOptionPrice(params));
  }, [
    spotPrice,
    strikePrice,
    interestRate,
    volatility,
    timeToExpiry,
    optionType,
    optionStyle,
    dividendYield
  ]);

  // Handle share button click
  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShowShareToast(true);
      setTimeout(() => setShowShareToast(false), 3000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  return (
    <div className="calculator-container pt-24">
      <div className="calculator-header text-center mb-8">
        <h1 className="text-2xl font-semibold text-[#113262] mb-2">Option Value Estimation Calculator</h1>
        <h2 className="text-lg text-gray-600">Estimate the theoretical price and Greeks of an options contract</h2>
      </div>

      <div className="max-w-5xl mx-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Sections */}
          <div className="lg:col-span-2 space-y-8">
            {/* Contract Details */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Contract Details</h2>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Spot Price
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('spot')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'spot' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Current market price of the underlying asset
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={spotPrice}
                    onChange={(e) => setSpotPrice(Math.max(1, Number(e.target.value)))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={1000}
                    max={50000}
                    step={100}
                    value={spotPrice}
                    onChange={(e) => setSpotPrice(Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>₹1,000</span>
                    <span>₹50,000</span>
                  </div>
                </div>

                <div>
                  <label className="block font-medium text-sm mb-1.5">Strike Price</label>
                  <input
                    type="number"
                    value={strikePrice}
                    onChange={(e) => setStrikePrice(Math.max(1, Number(e.target.value)))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={1000}
                    max={50000}
                    step={100}
                    value={strikePrice}
                    onChange={(e) => setStrikePrice(Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>₹1,000</span>
                    <span>₹50,000</span>
                  </div>
                </div>

                <div>
                  <label className="block font-medium text-sm mb-1.5">Days to Expiry</label>
                  <input
                    type="number"
                    value={timeToExpiry}
                    onChange={(e) => setTimeToExpiry(Math.max(1, Math.min(365, Number(e.target.value))))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={1}
                    max={365}
                    step={1}
                    value={timeToExpiry}
                    onChange={(e) => setTimeToExpiry(Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>1 day</span>
                    <span>365 days</span>
                  </div>
                </div>

                <div>
                  <label className="block font-medium text-sm mb-1.5">Lot Size</label>
                  <input
                    type="number"
                    value={lotSize}
                    onChange={(e) => setLotSize(Math.max(1, Number(e.target.value)))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={1}
                    max={1000}
                    step={1}
                    value={lotSize}
                    onChange={(e) => setLotSize(Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>1</span>
                    <span>1000</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Option Type */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Option Type</h2>
              <div className="grid grid-cols-1 gap-6">
                <div className="col-span-1">
                  <label className="block font-medium text-sm mb-3 flex items-center">
                    Select Option Type
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('optionType')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'optionType' && (
                        <div className="absolute z-10 left-6 -top-2 w-72 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Call options give the right to buy, Put options give the right to sell the underlying asset
                        </div>
                      )}
                    </div>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <div 
                      className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                        optionType === 'call' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setOptionType('call')}
                    >
                      <p className="font-medium">Call Option</p>
                      <p className="text-xs text-gray-500 mt-1">Right to buy the underlying</p>
                    </div>
                    <div 
                      className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                        optionType === 'put' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setOptionType('put')}
                    >
                      <p className="font-medium">Put Option</p>
                      <p className="text-xs text-gray-500 mt-1">Right to sell the underlying</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Volatility & Interest Rate */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Market Parameters</h2>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Volatility (%)
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('volatility')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'volatility' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Annualized volatility of the underlying asset
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={volatility}
                    onChange={(e) => setVolatility(Math.max(1, Math.min(200, Number(e.target.value))))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={1}
                    max={100}
                    step={0.5}
                    value={volatility}
                    onChange={(e) => setVolatility(Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>1%</span>
                    <span>100%</span>
                  </div>
                </div>

                <div>
                  <label className="block font-medium text-sm mb-1.5">Risk-Free Interest Rate (%)</label>
                  <input
                    type="number"
                    value={interestRate}
                    onChange={(e) => setInterestRate(Math.max(0, Math.min(30, Number(e.target.value))))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={0}
                    max={20}
                    step={0.1}
                    value={interestRate}
                    onChange={(e) => setInterestRate(Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0%</span>
                    <span>20%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Advanced Settings */}
            <div className="bg-white rounded-lg shadow">
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full p-4 flex justify-between items-center hover:bg-gray-50"
              >
                <h2 className="text-lg font-bold text-gray-900">Advanced Settings</h2>
                <ChevronDown 
                  className={`transform transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                  size={20}
                />
              </button>
              
              {showAdvanced && (
                <div className="p-4 border-t">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block font-medium text-sm mb-1.5">Option Style</label>
                      <div className="grid grid-cols-2 gap-2">
                        <div 
                          className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                            optionStyle === 'european' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                          }`}
                          onClick={() => setOptionStyle('european')}
                        >
                          <p className="font-medium">European</p>
                          <p className="text-xs text-gray-500 mt-1">Exercise at expiry only</p>
                        </div>
                        <div 
                          className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                            optionStyle === 'american' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                          }`}
                          onClick={() => setOptionStyle('american')}
                        >
                          <p className="font-medium">American</p>
                          <p className="text-xs text-gray-500 mt-1">Exercise any time</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block font-medium text-sm mb-1.5">Dividend Yield (%)</label>
                      <input
                        type="number"
                        value={dividendYield}
                        onChange={(e) => setDividendYield(Math.max(0, Math.min(20, Number(e.target.value))))}
                        className="w-full p-1.5 border rounded text-sm"
                      />
                      <input
                        type="range"
                        min={0}
                        max={20}
                        step={0.1}
                        value={dividendYield}
                        onChange={(e) => setDividendYield(Number(e.target.value))}
                        className="w-full mt-2"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>0%</span>
                        <span>20%</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Results Section */}
          <div className="bg-[#113262] text-white rounded-lg h-[500px] sticky top-6">
            <div className="p-4 border-b border-white/20">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold">Option Valuation</h3>
                <button onClick={handleShare} className="p-1 hover:bg-blue-700 rounded">
                  <Share size={18} />
                </button>
              </div>
            </div>

            <div className="p-4 flex flex-col h-[calc(100%-68px)] justify-between">
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-2xl font-bold mb-1">
                    {formatCurrency(result.optionPrice)}
                  </div>
                  <div className="text-sm text-gray-300">Theoretical Price per Share</div>
                  <div className="text-base font-medium mt-1">
                    {formatCurrency(result.optionPrice * lotSize)}
                    <span className="text-xs text-gray-300 ml-1">per lot ({lotSize} shares)</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Intrinsic Value</span>
                    <span className="font-bold">{formatCurrency(result.intrinsicValue)}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Time Value</span>
                    <span className="font-bold">{formatCurrency(result.timeValue)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Break-Even Price</span>
                    <span className="font-bold">{formatCurrency(result.breakEvenPrice)}</span>
                  </div>

                  <div className="py-2 border-t border-white/20">
                    <div className="text-sm font-medium mb-2">Greeks</div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                      <div className="flex justify-between">
                        <span className="text-xs">Delta</span>
                        <span className="text-xs font-semibold">{result.delta.toFixed(4)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs">Gamma</span>
                        <span className="text-xs font-semibold">{result.gamma.toFixed(6)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs">Theta</span>
                        <span className="text-xs font-semibold">{result.theta.toFixed(4)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs">Vega</span>
                        <span className="text-xs font-semibold">{result.vega.toFixed(4)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs">Rho</span>
                        <span className="text-xs font-semibold">{result.rho.toFixed(4)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <button className="w-full bg-orange-400 text-white py-2 rounded-lg mt-4 hover:bg-orange-500 transition-colors text-sm">
                Learn Options Trading →
              </button>
            </div>
          </div>

          {showShareToast && (
            <div className="fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded shadow-lg">
              Link copied!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OptionValueCalculator;